/**
 * ADL Motion Engine
 * 外部ライブラリ非依存の共通モーションエンジン。
 * window.ADLMotion.init(root) のみを公開する。
 */
(function () {
  'use strict';

  var JP_RE = /[぀-ヿ一-龯]/;

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function isMobile() {
    return window.innerWidth < 768;
  }

  // ---------------------------------------------------------------------
  // (a) word-split
  // ---------------------------------------------------------------------

  function wrapWordSpan(text, index) {
    var word = document.createElement('span');
    word.className = 'adl-word';
    var inner = document.createElement('span');
    inner.className = 'adl-word__in';
    inner.style.setProperty('--i', index);
    inner.textContent = text;
    word.appendChild(inner);
    return word;
  }

  // Recursively process child nodes, splitting text nodes into words,
  // preserving element nodes (e.g. <em>, <br>) and recursing into them.
  function splitWordsInNode(node, counterRef) {
    var frag = document.createDocumentFragment();
    var children = Array.prototype.slice.call(node.childNodes);

    children.forEach(function (child) {
      if (child.nodeType === Node.TEXT_NODE) {
        var text = child.textContent;
        // split by whitespace but keep the spaces
        var parts = text.split(/(\s+)/).filter(function (p) {
          return p.length > 0;
        });
        parts.forEach(function (part) {
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            frag.appendChild(wrapWordSpan(part, counterRef.i++));
          }
        });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.tagName === 'BR') {
          frag.appendChild(child.cloneNode(true));
        } else {
          var clone = child.cloneNode(false);
          var innerFrag = splitWordsInNode(child, counterRef);
          clone.appendChild(innerFrag);
          frag.appendChild(clone);
        }
      }
    });

    return frag;
  }

  function initWordSplit(el) {
    var raw = el.textContent || '';
    var isJapanese = JP_RE.test(raw);

    if (isJapanese) {
      // 日本語は分かち書き(単語区切り)が無いため、文字単位のアニメーションにする
      initCharSplit(el);
      return;
    }

    var stagger = el.getAttribute('data-adl-stagger') || '0.016';
    el.style.setProperty('--adl-stagger', stagger + 's');

    var counterRef = { i: 0 };
    var frag = splitWordsInNode(el, counterRef);
    el.textContent = '';
    el.appendChild(frag);

    observeReveal(el);
  }

  // ---------------------------------------------------------------------
  // (a-2) char-split — 日本語テキスト用。word-split と見た目は同じ
  // (下から傾いて迫り上がる) だが、文字単位で分割する。
  // ---------------------------------------------------------------------

  function wrapCharSpan(ch, index) {
    var charEl = document.createElement('span');
    charEl.className = 'adl-word'; // word-split と同じ見た目のCSSを再利用
    var inner = document.createElement('span');
    inner.className = 'adl-word__in';
    inner.style.setProperty('--i', index);
    inner.textContent = ch;
    charEl.appendChild(inner);
    return charEl;
  }

  // word-split の splitWordsInNode と同じ再帰構造だが、空白以外は
  // 1書記素(絵文字等のサロゲートペアを壊さないよう Array.from を使用)ずつ分割する。
  function splitCharsInNode(node, counterRef) {
    var frag = document.createDocumentFragment();
    var children = Array.prototype.slice.call(node.childNodes);

    children.forEach(function (child) {
      if (child.nodeType === Node.TEXT_NODE) {
        var text = child.textContent;
        var parts = text.split(/(\s+)/).filter(function (p) {
          return p.length > 0;
        });
        parts.forEach(function (part) {
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            Array.from(part).forEach(function (ch) {
              frag.appendChild(wrapCharSpan(ch, counterRef.i++));
            });
          }
        });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.tagName === 'BR') {
          frag.appendChild(child.cloneNode(true));
        } else {
          var clone = child.cloneNode(false);
          var innerFrag = splitCharsInNode(child, counterRef);
          clone.appendChild(innerFrag);
          frag.appendChild(clone);
        }
      }
    });

    return frag;
  }

  function initCharSplit(el) {
    // 文字数が多い(長文段落)ほど1文字あたりのstaggerを詰めて、
    // 全体の登場時間が間延びしないようにする。
    var stagger = el.getAttribute('data-adl-stagger');
    if (!stagger) {
      var len = (el.textContent || '').replace(/\s+/g, '').length;
      stagger = len > 60 ? '0.006' : len > 24 ? '0.012' : '0.02';
    }
    el.style.setProperty('--adl-stagger', stagger + 's');

    var counterRef = { i: 0 };
    var frag = splitCharsInNode(el, counterRef);
    el.textContent = '';
    el.appendChild(frag);

    observeReveal(el);
  }

  // ---------------------------------------------------------------------
  // (b) line-mask
  // ---------------------------------------------------------------------

  function wrapLine(node, index) {
    var line = document.createElement(node.tagName || 'div');
    // Keep block behaviour regardless of original tag via CSS class.
    line.className = 'adl-line';
    var inner = document.createElement('span');
    inner.className = 'adl-line__in';
    inner.style.setProperty('--i', index);

    while (node.firstChild) {
      inner.appendChild(node.firstChild);
    }
    line.appendChild(inner);
    return line;
  }

  function initLineMask(el) {
    var stagger = el.getAttribute('data-adl-stagger') || '0.08';
    el.style.setProperty('--adl-stagger', stagger + 's');

    var childEls = Array.prototype.filter.call(el.children, function (c) {
      return c.nodeType === Node.ELEMENT_NODE;
    });

    if (childEls.length === 0) {
      // 対象自身をラインとして扱う
      var content = document.createDocumentFragment();
      while (el.firstChild) {
        content.appendChild(el.firstChild);
      }
      var line = document.createElement('span');
      line.className = 'adl-line';
      var inner = document.createElement('span');
      inner.className = 'adl-line__in';
      inner.style.setProperty('--i', 0);
      inner.appendChild(content);
      line.appendChild(inner);
      el.appendChild(line);
    } else {
      childEls.forEach(function (child, index) {
        var lineWrap = document.createElement('span');
        lineWrap.className = 'adl-line';
        var inner = document.createElement('span');
        inner.className = 'adl-line__in';
        inner.style.setProperty('--i', index);

        // move child's content into inner, preserving child's own tag via style
        var attrs = child.attributes;
        while (child.firstChild) {
          inner.appendChild(child.firstChild);
        }
        lineWrap.appendChild(inner);
        el.replaceChild(lineWrap, child);
      });
    }

    observeReveal(el);
  }

  // ---------------------------------------------------------------------
  // (c) fade-up
  // ---------------------------------------------------------------------

  function initFadeUp(el) {
    observeReveal(el);
  }

  // ---------------------------------------------------------------------
  // (f) reveal-img (SPEC 4-D)
  // ---------------------------------------------------------------------

  function initRevealImg(el) {
    observeReveal(el);
  }

  // ---------------------------------------------------------------------
  // IntersectionObserver 共通処理 (a, b, c, f 用)
  // ---------------------------------------------------------------------

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    return revealObserver;
  }

  // data-adl-trigger="<CSSセレクタ>" が指定されている場合、自要素ではなく
  // 同一セクション内でそのセレクタに一致する要素の交差を監視し、
  // そちらが交差したときに対象へ is-inview を付ける。
  // 属性がない場合は従来通り自要素を監視する(A/B/C/D への影響なし)。
  function observeReveal(el) {
    if (prefersReducedMotion()) {
      el.classList.add('is-inview');
      return;
    }

    var triggerSelector = el.getAttribute('data-adl-trigger');
    if (triggerSelector) {
      var scopeEl = el.closest('section') || document;
      var triggerEl = scopeEl.querySelector(triggerSelector);
      if (triggerEl) {
        var triggerObserver = new IntersectionObserver(
          function (entries, observer) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                el.classList.add('is-inview');
                observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0, rootMargin: '0px 0px -10% 0px' }
        );
        triggerObserver.observe(triggerEl);
        return;
      }
      // セレクタに一致する要素が見つからない場合は従来通り自要素を監視する
    }

    getRevealObserver().observe(el);
  }

  // ---------------------------------------------------------------------
  // (d) scale-img — rAF + scroll で連続駆動
  // ---------------------------------------------------------------------

  var scaleImgEls = [];
  var scaleLoopRunning = false;
  var scaleTicking = false;

  function computeScaleProgress(rect, viewportH) {
    // p=0: 要素が画面下端に入り始める(rect.top === viewportH)
    // p=1: 要素が画面上端を抜け切る(rect.top === -rect.height)
    var p = 1 - (rect.top + rect.height) / (viewportH + rect.height);
    if (p < 0) p = 0;
    if (p > 1) p = 1;
    return p;
  }

  function sineInOut(p) {
    return -(Math.cos(Math.PI * p) - 1) / 2;
  }

  function getScaleProgressRect(el) {
    var scopeSelector = el.getAttribute('data-adl-scale-scope');
    if (scopeSelector) {
      var scopeEl = el.closest(scopeSelector);
      if (scopeEl) return scopeEl.getBoundingClientRect();
    }
    return el.getBoundingClientRect();
  }

  function updateScaleImgEls() {
    var viewportH = window.innerHeight;
    var mobile = isMobile();

    scaleImgEls.forEach(function (el) {
      var rect = getScaleProgressRect(el);
      // 画面外(上下に大きく離れている)ならスキップ
      if (rect.bottom < -viewportH || rect.top > viewportH * 2) {
        return;
      }
      var p = computeScaleProgress(rect, viewportH);
      var eased = sineInOut(p);

      var maxAttr = mobile
        ? el.getAttribute('data-adl-scale-sp')
        : el.getAttribute('data-adl-scale-pc');
      var max = maxAttr ? parseFloat(maxAttr) : mobile ? 1.15 : 1.35;

      var scale = 1 + eased * (max - 1);
      el.style.transform = 'scale(' + scale + ')';
    });
  }

  function scaleLoop() {
    if (!scaleTicking) {
      scaleLoopRunning = false;
      return;
    }
    updateScaleImgEls();
    scaleTicking = false;
    requestAnimationFrame(scaleLoop);
  }

  function onScrollOrResize() {
    if (scaleImgEls.length === 0) return;
    scaleTicking = true;
    if (!scaleLoopRunning) {
      scaleLoopRunning = true;
      requestAnimationFrame(scaleLoop);
    }
  }

  var scrollListenerAttached = false;

  function initScaleImg(el) {
    el.style.transformOrigin = 'bottom';
    el.style.willChange = 'transform';

    if (prefersReducedMotion()) {
      var max = isMobile() ? 1.0 : 1.0;
      el.style.transform = 'scale(1)';
      return;
    }

    scaleImgEls.push(el);

    if (!scrollListenerAttached) {
      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
      scrollListenerAttached = true;
    }

    // 初回計算
    scaleTicking = true;
    if (!scaleLoopRunning) {
      scaleLoopRunning = true;
      requestAnimationFrame(scaleLoop);
    }
  }

  // ---------------------------------------------------------------------
  // init
  // ---------------------------------------------------------------------

  var HANDLERS = {
    'word-split': initWordSplit,
    'line-mask': initLineMask,
    'fade-up': initFadeUp,
    'scale-img': initScaleImg,
    'reveal-img': initRevealImg
  };

  function init(root) {
    var scope = root || document;
    var targets = scope.querySelectorAll('[data-adl-anime]');

    targets.forEach(function (el) {
      if (el.getAttribute('data-adl-init') === 'true') return;
      el.setAttribute('data-adl-init', 'true');

      var type = el.getAttribute('data-adl-anime');
      var handler = HANDLERS[type];
      if (handler) {
        try {
          handler(el);
        } catch (err) {
          // 1つの要素で例外が起きても他のセクションの初期化を止めない。
          // (何も処理されないと CSS 側の初期非表示状態のまま = テキストが消えて見える)
          el.classList.add('is-inview');
          if (window.console && console.error) {
            console.error('[ADLMotion] init failed for', el, err);
          }
        }
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target || document);
  });

  window.ADLMotion = {
    init: init
  };
})();